import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { humanizeAxiosError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PW_HINT = "En az 8 karakter, 1 büyük, 1 küçük, 1 rakam. Ardışık (123, abc) veya tekrar (aaa) yok.";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState("identifier"); // 'identifier' | 'code'
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const request = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      setStep("code");
      toast.success(data.message || "Doğrulama kodu kayıtlı cep telefonunuza SMS ile gönderildi");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!code || code.length < 6) { toast.error("6 haneli kodu girin"); return; }
    if (newPassword !== confirmPassword) { toast.error("Şifreler uyuşmuyor"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password-by-code", {
        phone: identifier, code, new_password: newPassword,
      });
      toast.success("Şifreniz güncellendi. Giriş yapabilirsiniz.");
      nav("/login");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Colossae Personel</div>
            <h1 className="font-display text-3xl font-bold mt-1">Şifremi Unuttum</h1>
            <div className="text-sm text-muted-foreground mt-1">
              {step === "identifier"
                ? "Sisteme kayıtlı cep telefonunuzu başında 0 olacak şekilde girin (0XXXXXXXXXX). 6 haneli doğrulama kodu SMS ile gönderilecek."
                : `Cep telefonunuza SMS ile gönderdiğimiz 6 haneli kodu ve yeni şifrenizi girin.`}
            </div>
          </div>

          {step === "identifier" ? (
            <form onSubmit={request} className="space-y-3">
              <div>
                <Label>Cep Telefonu</Label>
                <Input required inputMode="tel" value={identifier} onChange={(e) => setIdentifier(e.target.value)} data-testid="fp-identifier" placeholder="0XXXXXXXXXX" />
                <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                  Doğrulama kodu, kayıtlı cep telefonunuza <b>SMS</b> ile gönderilir.
                  Numaranızı <b>başında 0</b> olacak şekilde girin (örn. 05XX XXX XX XX).
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="fp-request">
                {loading ? "Gönderiliyor…" : "SMS ile Kod Gönder"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Kod (6 hane)</Label>
                <Input required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="fp-code" placeholder="123456" className="tracking-widest text-center text-lg" />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Kodu cep telefonunuza gelen SMS'ten alabilirsiniz.
                </div>
              </div>
              <div>
                <Label>Yeni Şifre</Label>
                <Input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="fp-new-password" />
                <div className="text-[11px] text-muted-foreground mt-1">{PW_HINT}</div>
              </div>
              <div>
                <Label>Yeni Şifre (Tekrar)</Label>
                <Input type="password" required minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="fp-confirm-password" />
                {confirmPassword && confirmPassword !== newPassword && (
                  <div className="text-[11px] text-destructive mt-1">Şifreler uyuşmuyor</div>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setStep("identifier")} disabled={loading} data-testid="fp-back">Geri</Button>
                <Button type="submit" className="flex-1" disabled={loading} data-testid="fp-submit">
                  {loading ? "Kaydediliyor…" : "Şifreyi Değiştir"}
                </Button>
              </div>
              <button
                type="button"
                className="text-xs text-primary underline block mx-auto"
                onClick={() => request({ preventDefault: () => {} })}
                data-testid="fp-resend"
              >
                Kod gelmediyse yeniden gönder
              </button>
            </form>
          )}

          <Link to="/login" className="text-sm text-primary underline block text-center" data-testid="fp-back-login">Girişe dön</Link>
        </CardContent>
      </Card>
    </div>
  );
}
